import React from 'react';
import type { FlowBlock, AgentBlock, FunctionBlock, RouterBlock, OutputBlock, MemoryBlock, SetBlock, CodeBlock, CompactionBlock, TransformBlock, FlowNodeRole } from '../../types/flow';

const ALL_METHODS: string[] = [
  'files.read', 'files.write', 'files.edit', 'files.glob', 'files.grep',
  'shell.exec',
  'medical.vitals_query', 'medical.alert_trigger', 'medical.record_query',
  'gmail.send', 'gmail.read', 'gmail.template',
  'scheduling.followup_create', 'scheduling.calendar_check',
  'logging.create', 'logging.search', 'logging.update',
  'memory.search', 'memory.list', 'memory.create', 'memory.update', 'memory.delete',
  'memory.working.push', 'memory.working.set', 'memory.working.clear',
];

const ROLE_OPTIONS: FlowNodeRole[] = [
  'general_assistant',
];

interface PropertiesPanelProps {
  block: FlowBlock | null;
  onChange: (updated: FlowBlock) => void;
  onDelete: () => void;
  isEntryNode: boolean;
  onSetEntry: () => void;
}

export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({ block, onChange, onDelete, isEntryNode, onSetEntry }) => {
  if (!block) {
    return (
      <div className="h-full flex items-center justify-center p-4 text-center">
        <p className="text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
          Select a block to edit its properties
        </p>
      </div>
    );
  }

  const update = (patch: Partial<FlowBlock>) => {
    onChange({ ...block, ...patch } as FlowBlock);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-3 py-2 border-b dark:border-claude-darkBorder border-claude-border">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold dark:text-claude-darkText text-claude-text">
            Properties
          </h3>
          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 dark:text-claude-darkTextSecondary text-claude-textSecondary">
            {block.type}
          </span>
        </div>
        <p className="text-xs mt-1 dark:text-claude-darkTextSecondary text-claude-textSecondary font-mono">
          {block.id}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        <Field label="Label">
          <input
            type="text"
            value={block.label || ''}
            onChange={(e) => update({ label: e.target.value } as any)}
            className="input-base"
            placeholder="Block label"
          />
        </Field>

        {block.type === 'agent' && <AgentProperties block={block as AgentBlock} update={update} />}
        {block.type === 'function' && <FunctionProperties block={block as FunctionBlock} update={update} />}
        {block.type === 'router' && <RouterProperties block={block as RouterBlock} update={update} />}
        {block.type === 'output' && <OutputProperties block={block as OutputBlock} update={update} />}
        {block.type === 'memory' && <MemoryProperties block={block as MemoryBlock} update={update} />}
        {block.type === 'set' && <SetProperties block={block as SetBlock} update={update} />}
        {block.type === 'code' && <CodeProperties block={block as CodeBlock} update={update} />}
        {block.type === 'compaction' && <CompactionProperties block={block as CompactionBlock} update={update} />}
        {block.type === 'transform' && <TransformProperties block={block as TransformBlock} update={update} />}
      </div>

      <div className="px-3 py-2 border-t dark:border-claude-darkBorder border-claude-border space-y-2">
        <button
          type="button"
          onClick={onSetEntry}
          disabled={isEntryNode}
          className={`w-full text-xs py-1.5 rounded font-medium transition-colors ${
            isEntryNode
              ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 cursor-default'
              : 'bg-gray-100 dark:bg-gray-800 dark:text-claude-darkText text-claude-text hover:bg-gray-200 dark:hover:bg-gray-700'
          }`}
        >
          {isEntryNode ? '✓ Entry Node' : 'Set as Entry Node'}
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="w-full text-xs py-1.5 rounded font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          Delete Block
        </button>
      </div>
    </div>
  );
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1 dark:text-claude-darkTextSecondary text-claude-textSecondary">
        {label}
      </label>
      {children}
    </div>
  );
}

function AgentProperties({ block, update }: { block: AgentBlock; update: (p: Partial<FlowBlock>) => void }) {
  const toggleMethod = (method: string) => {
    const current = block.allowedMethods || [];
    const next = current.includes(method)
      ? current.filter((m) => m !== method)
      : [...current, method];
    update({ allowedMethods: next } as any);
  };

  return (
    <>
      <Field label="Role (custom or preset)">
        <input
          type="text"
          list="role-presets"
          value={block.role || ''}
          onChange={(e) => update({ role: e.target.value || undefined } as any)}
          className="input-base"
          placeholder="e.g. medical_analyst, my_custom_agent, ..."
        />
        <datalist id="role-presets">
          {ROLE_OPTIONS.map((r) => (
            <option key={r} value={r} />
          ))}
        </datalist>
        <p className="text-[10px] mt-1 dark:text-claude-darkTextSecondary text-claude-textSecondary">
          Predefined roles get default method permissions. Custom roles use the Allowed Methods below as the permission set.
        </p>
      </Field>

      <Field label="Prompt">
        <textarea
          value={block.prompt || ''}
          onChange={(e) => update({ prompt: e.target.value } as any)}
          className="input-base min-h-[80px] font-mono text-xs"
          placeholder="System prompt for this agent..."
        />
      </Field>

      <Field label="Allowed Methods">
        <div className="space-y-1 max-h-48 overflow-y-auto rounded border dark:border-claude-darkBorder border-claude-border p-2">
          {ALL_METHODS.map((method) => (
            <label key={method} className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={(block.allowedMethods || []).includes(method)}
                onChange={() => toggleMethod(method)}
                className="!w-3.5 !h-3.5"
              />
              <span className="dark:text-claude-darkText text-claude-text">{method}</span>
            </label>
          ))}
        </div>
      </Field>

      <Field label="Model Override">
        <input
          type="text"
          value={block.model || ''}
          onChange={(e) => update({ model: e.target.value || undefined } as any)}
          className="input-base"
          placeholder="Use default model"
        />
      </Field>

      <Field label={`Max Rounds: ${block.maxRounds || 25}`}>
        <input
          type="range"
          min={1}
          max={50}
          value={block.maxRounds || 25}
          onChange={(e) => update({ maxRounds: Number(e.target.value) } as any)}
          className="w-full"
        />
      </Field>

      <Field label={`Temperature: ${block.temperature ?? 0.7}`}>
        <input
          type="range"
          min={0}
          max={2}
          step={0.1}
          value={block.temperature ?? 0.7}
          onChange={(e) => update({ temperature: Number(e.target.value) } as any)}
          className="w-full"
        />
      </Field>

      <div className="space-y-2 pt-1 border-t dark:border-claude-darkBorder/50 border-claude-border/50">
        <p className="text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary pt-1">Compaction</p>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={block.compactOnOverflow ?? true}
            onChange={(e) => update({ compactOnOverflow: e.target.checked } as any)}
            className="!w-3.5 !h-3.5"
          />
          <span className="dark:text-claude-darkText text-claude-text">Compact on overflow</span>
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={block.extractFactsOnCompact ?? true}
            onChange={(e) => update({ extractFactsOnCompact: e.target.checked } as any)}
            className="!w-3.5 !h-3.5"
          />
          <span className="dark:text-claude-darkText text-claude-text">Extract facts on compact</span>
        </label>
      </div>
    </>
  );
}

function FunctionProperties({ block, update }: { block: FunctionBlock; update: (p: Partial<FlowBlock>) => void }) {
  const paramsStr = Object.keys(block.params).length > 0
    ? JSON.stringify(block.params, null, 2)
    : '';

  return (
    <>
      <Field label="Method">
        <select
          value={block.method}
          onChange={(e) => update({ method: e.target.value } as any)}
          className="input-base"
        >
          {ALL_METHODS.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </Field>

      <Field label="Params (JSON)">
        <textarea
          value={paramsStr}
          onChange={(e) => {
            try {
              const parsed = e.target.value.trim() ? JSON.parse(e.target.value) : {};
              update({ params: parsed } as any);
            } catch { /* ignore invalid JSON while typing */ }
          }}
          className="input-base min-h-[100px] font-mono text-xs"
          placeholder='{"key": "value", "template": "{{state.input}}"}'
        />
      </Field>
      <p className="text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary">
        Use {`{{state.path.to.value}}`} for template substitution from flow state.
      </p>
    </>
  );
}

function RouterProperties({ block, update }: { block: RouterBlock; update: (p: Partial<FlowBlock>) => void }) {
  const branchEntries = Object.entries(block.branches);

  const updateBranch = (oldKey: string, newKey: string, target: string) => {
    const newBranches = { ...block.branches };
    delete newBranches[oldKey];
    if (newKey) newBranches[newKey] = target;
    update({ branches: newBranches } as any);
  };

  const addBranch = () => {
    const key = `branch-${Date.now().toString(36)}`;
    update({ branches: { ...block.branches, [key]: '' } } as any);
  };

  const removeBranch = (key: string) => {
    const newBranches = { ...block.branches };
    delete newBranches[key];
    update({ branches: newBranches } as any);
  };

  return (
    <>
      <Field label="Evaluate Type">
        <select
          value={block.evaluate.type}
          onChange={(e) => update({ evaluate: { ...block.evaluate, type: e.target.value as 'state' | 'llm' } } as any)}
          className="input-base"
        >
          <option value="state">State Path</option>
          <option value="llm">LLM Decision</option>
        </select>
      </Field>

      {block.evaluate.type === 'state' && (
        <Field label="State Path">
          <input
            type="text"
            value={block.evaluate.path || ''}
            onChange={(e) => update({ evaluate: { ...block.evaluate, path: e.target.value } } as any)}
            className="input-base font-mono text-xs"
            placeholder="outputs.triage.severity"
          />
        </Field>
      )}

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
            Branches
          </label>
          <button
            type="button"
            onClick={addBranch}
            className="text-xs text-claude-accent hover:underline"
          >
            + Add
          </button>
        </div>
        <div className="space-y-2">
          {branchEntries.map(([key, target]) => (
            <div key={key} className="flex items-center gap-1">
              <input
                type="text"
                value={key}
                onChange={(e) => updateBranch(key, e.target.value, target)}
                className="input-base flex-1 text-xs"
                placeholder="condition"
              />
              <span className="text-xs text-gray-400">→</span>
              <input
                type="text"
                value={target}
                onChange={(e) => updateBranch(key, key, e.target.value)}
                className="input-base flex-1 text-xs"
                placeholder="node-id"
              />
              <button
                type="button"
                onClick={() => removeBranch(key)}
                className="text-red-400 hover:text-red-500 text-xs px-1"
              >
                ✕
              </button>
            </div>
          ))}
          {branchEntries.length === 0 && (
            <p className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary italic">
              No branches. Add one to route.
            </p>
          )}
        </div>
      </div>

      <Field label="Default Target">
        <input
          type="text"
          value={block.default || ''}
          onChange={(e) => update({ default: e.target.value || undefined } as any)}
          className="input-base text-xs"
          placeholder="fallback node-id"
        />
      </Field>
    </>
  );
}

function OutputProperties({ block, update }: { block: OutputBlock; update: (p: Partial<FlowBlock>) => void }) {
  return (
    <Field label="Format Template">
      <textarea
        value={block.format || ''}
        onChange={(e) => update({ format: e.target.value || undefined } as any)}
        className="input-base min-h-[80px] font-mono text-xs"
        placeholder="Optional: {{state.outputs.agent.response}}"
      />
      <p className="text-[10px] mt-1 dark:text-claude-darkTextSecondary text-claude-textSecondary">
        Use {`{{state.path}}`} for template substitution. Leave empty to use last agent output.
      </p>
    </Field>
  );
}

function MemoryProperties({ block, update }: { block: MemoryBlock; update: (p: Partial<FlowBlock>) => void }) {
  return (
    <>
      <Field label="Action">
        <select
          value={block.action}
          onChange={(e) => update({ action: e.target.value as 'extract' | 'retrieve' } as any)}
          className="input-base"
        >
          <option value="extract">Extract (auto-extract facts from conversation)</option>
          <option value="retrieve">Retrieve (search and inject memories)</option>
        </select>
      </Field>

      <Field label={`Max Items: ${block.maxItems ?? 12}`}>
        <input
          type="range"
          min={1}
          max={60}
          value={block.maxItems ?? 12}
          onChange={(e) => update({ maxItems: Number(e.target.value) } as any)}
          className="w-full"
        />
      </Field>

      <Field label={`Min Confidence: ${block.minConfidence ?? 0.5}`}>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={block.minConfidence ?? 0.5}
          onChange={(e) => update({ minConfidence: Number(e.target.value) } as any)}
          className="w-full"
        />
      </Field>

      <label className="flex items-center gap-2 text-xs cursor-pointer">
        <input
          type="checkbox"
          checked={block.judge ?? true}
          onChange={(e) => update({ judge: e.target.checked } as any)}
          className="!w-3.5 !h-3.5"
        />
        <span className="dark:text-claude-darkText text-claude-text">Use LLM Judge (quality filter)</span>
      </label>

      <Field label="Model Override">
        <input
          type="text"
          value={block.model || ''}
          onChange={(e) => update({ model: e.target.value || undefined } as any)}
          className="input-base"
          placeholder="Use default extraction model"
        />
      </Field>
    </>
  );
}

function SetProperties({ block, update }: { block: SetBlock; update: (p: Partial<FlowBlock>) => void }) {
  const entries = Object.entries(block.values);

  const updateKey = (oldKey: string, newKey: string, value: string) => {
    const next = { ...block.values };
    delete next[oldKey];
    if (newKey) next[newKey] = value;
    update({ values: next } as any);
  };

  const addEntry = () => {
    const key = `key-${Date.now().toString(36)}`;
    update({ values: { ...block.values, [key]: '' } } as any);
  };

  const removeEntry = (key: string) => {
    const next = { ...block.values };
    delete next[key];
    update({ values: next } as any);
  };

  return (
    <>
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
            Values (key → template)
          </label>
          <button type="button" onClick={addEntry} className="text-xs text-claude-accent hover:underline">+ Add</button>
        </div>
        <div className="space-y-2">
          {entries.map(([key, value]) => (
            <div key={key} className="flex items-center gap-1">
              <input type="text" value={key} onChange={(e) => updateKey(key, e.target.value, value)} className="input-base flex-1 text-xs" placeholder="key" />
              <span className="text-xs text-gray-400">→</span>
              <input type="text" value={value} onChange={(e) => updateKey(key, key, e.target.value)} className="input-base flex-1 text-xs font-mono" placeholder="{{state.path}}" />
              <button type="button" onClick={() => removeEntry(key)} className="text-red-400 hover:text-red-500 text-xs px-1">✕</button>
            </div>
          ))}
          {entries.length === 0 && <p className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary italic">No values. Add one to set state.</p>}
        </div>
      </div>
      <p className="text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary">Use {`{{state.path.to.value}}`} for template substitution. Values are written to outputs.{block.id}.</p>
    </>
  );
}

function CodeProperties({ block, update }: { block: CodeBlock; update: (p: Partial<FlowBlock>) => void }) {
  return (
    <>
      <Field label="Code (JavaScript)">
        <textarea value={block.code} onChange={(e) => update({ code: e.target.value } as any)} className="input-base min-h-[160px] font-mono text-xs" placeholder="// inputs = { input, outputs, messages, workingMemory, context }" />
      </Field>
      <p className="text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary">The function receives <code>inputs</code>. Return an object — it becomes outputs.{block.id}.</p>
      <Field label={`Timeout: ${block.timeout ?? 5000}ms`}>
        <input type="range" min={1000} max={30000} step={1000} value={block.timeout ?? 5000} onChange={(e) => update({ timeout: Number(e.target.value) } as any)} className="w-full" />
      </Field>
    </>
  );
}

function CompactionProperties({ block, update }: { block: CompactionBlock; update: (p: Partial<FlowBlock>) => void }) {
  return (
    <>
      <Field label={`Compaction Threshold: ${Math.round((block.threshold ?? 0.75) * 100)}% of context window`}>
        <input type="range" min={0.1} max={1.0} step={0.05} value={block.threshold ?? 0.75} onChange={(e) => update({ threshold: Number(e.target.value) } as any)} className="w-full" />
      </Field>
      <Field label={`Keep Recent Messages: ${block.keepRecent ?? 6}`}>
        <input type="range" min={1} max={20} value={block.keepRecent ?? 6} onChange={(e) => update({ keepRecent: Number(e.target.value) } as any)} className="w-full" />
      </Field>
      <label className="flex items-center gap-2 text-xs cursor-pointer">
        <input type="checkbox" checked={block.extractFacts ?? true} onChange={(e) => update({ extractFacts: e.target.checked } as any)} className="!w-3.5 !h-3.5" />
        <span className="dark:text-claude-darkText text-claude-text">Extract facts to memory before compacting</span>
      </label>
      <p className="text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary">Compacts message history when tokens exceed threshold. Skips if below threshold.</p>
    </>
  );
}

function TransformProperties({ block, update }: { block: TransformBlock; update: (p: Partial<FlowBlock>) => void }) {
  const entries = Object.entries(block.mapping);
  const updateKey = (oldKey: string, newKey: string, value: string) => {
    const next = { ...block.mapping };
    delete next[oldKey];
    if (newKey) next[newKey] = value;
    update({ mapping: next } as any);
  };
  const addEntry = () => { const key = `field-${Date.now().toString(36)}`; update({ mapping: { ...block.mapping, [key]: '' } } as any); };
  const removeEntry = (key: string) => { const next = { ...block.mapping }; delete next[key]; update({ mapping: next } as any); };

  return (
    <>
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">Mapping (output key ← source template)</label>
          <button type="button" onClick={addEntry} className="text-xs text-claude-accent hover:underline">+ Add</button>
        </div>
        <div className="space-y-2">
          {entries.map(([key, value]) => (
            <div key={key} className="flex items-center gap-1">
              <input type="text" value={key} onChange={(e) => updateKey(key, e.target.value, value)} className="input-base flex-1 text-xs" placeholder="outputKey" />
              <span className="text-xs text-gray-400">←</span>
              <input type="text" value={value} onChange={(e) => updateKey(key, key, e.target.value)} className="input-base flex-1 text-xs font-mono" placeholder="{{state.outputs.agent.response}}" />
              <button type="button" onClick={() => removeEntry(key)} className="text-red-400 hover:text-red-500 text-xs px-1">✕</button>
            </div>
          ))}
          {entries.length === 0 && <p className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary italic">No mappings.</p>}
        </div>
      </div>
      <p className="text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary">Use {`{{state.path}}`} for templates. Exact {`{{state.path}}`} preserves raw value (not stringified).</p>
    </>
  );
}